import { useEffect, useState } from 'react'

import { api, fehlertext } from '../api'
import { useLaden } from '../hooks'
import ArtikelAuswahl from '../komponenten/ArtikelAuswahl'
import Meldung from '../komponenten/Meldung'
import { MONATE_KURZ, MONATE_LANG, menge, zahlLesen, zahlText } from '../format'
import { aktiveFertigartikel } from '../hilfen'
import type { Artikel, PlanIst, Planmenge } from '../typen'

/**
 * Der Forecast des Chefs und das Blatt Plan - Ist: die Kurve, über die die Reichweite rechnet.
 *
 * Bis zum 04.09.2026 stand beides unter „Lieferanten" - dort hat es niemand gesucht. Der Forecast
 * ist eine Stammdatenpflege je Fertigartikel und Monat, Plan - Ist der Blick auf dasselbe Blatt
 * mit Vorjahr und tatsächlicher Produktion. Schneller geht ein einzelner Monat über die Seite
 * Regeln oder den Assistenten in einem Satz („Forecast Sanna Würfel Oktober 60 t").
 */

const QUELLE_FORECAST = 'forecast_gf'

export default function ForecastSeite() {
  const { daten: artikelDaten, fehler } = useLaden<Artikel[]>(() => api.artikel(), [])
  const artikel = artikelDaten ?? []

  return (
    <>
      <h2>Forecast und Plan-Ist</h2>
      <p className="hinweis" style={{ maxWidth: '80ch' }}>
        Der Forecast des Chefs je Fertigartikel und Monat - die Spalte „FC GF" der Datei PLAN - IST. Die Disposition
        rechnet die Reichweite über diese Kurve. Darunter das Blatt Plan - Ist mit Vorjahr, Forecast und tatsächlicher
        Produktion.
      </p>
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
      <ForecastEditor artikel={aktiveFertigartikel(artikel)} />
      <PlanIstTabelle />
    </>
  )
}

/** Schlüssel einer Zelle: Artikel und Monat. */
function zelle(nummer: string, monat: number): string {
  return `${nummer}:${monat}`
}

/** Ein Monatswert für das Eingabefeld: drei Nachkommastellen, kein „34,168000000000006" aus der Fließkommarechnung. */
function mengeText(wert: number | null | undefined): string {
  if (wert === null || wert === undefined) return ''
  return zahlText(Math.round(wert * 1000) / 1000)
}

/**
 * Der Forecast des Chefs je Monat - die Spalte „FC GF" der Datei PLAN - IST, hier eingetippt.
 *
 * Zeigt und schreibt nur die Quelle „forecast_gf"; Kontraktmengen des Regel-Agenten bleiben
 * unberührt. Ein geleertes Feld löscht den Monat beim Speichern, damit wieder der flache Satz gilt.
 */
function ForecastEditor({ artikel }: { artikel: Artikel[] }) {
  const [jahr, setJahr] = useState(new Date().getFullYear())
  const [werte, setWerte] = useState<Record<string, string>>({})
  const [gespeichert, setGespeichert] = useState<Set<string>>(new Set())
  const [meldung, setMeldung] = useState<string | null>(null)

  function uebernehmen(liste: Planmenge[]) {
    const neu: Record<string, string> = {}
    for (const p of liste) neu[zelle(p.fertigartikel, p.monat)] = mengeText(p.menge_t)
    setWerte(neu)
    setGespeichert(new Set(Object.keys(neu)))
  }

  useEffect(() => {
    api
      .planmengen(undefined, jahr, QUELLE_FORECAST)
      .then(uebernehmen)
      .catch(() => uebernehmen([]))
  }, [jahr])

  async function einlesen(datei: File) {
    setMeldung('Datei wird gelesen.')
    try {
      const r = await api.planmengenExcel(datei)
      const liste = await api.planmengen(undefined, jahr, QUELLE_FORECAST)
      uebernehmen(liste)
      setMeldung(
        `${r.gesetzt} Monatswerte für ${r.fertigartikel.length} Fertigartikel (${r.jahre.join(', ')}) übernommen.` +
          (r.unbekannt.length ? ` Nicht als Fertigartikel bekannt: ${r.unbekannt.join(', ')}.` : ''),
      )
    } catch (f) {
      setMeldung(fehlertext(f))
    }
  }

  async function speichern() {
    const daten: Planmenge[] = []
    let geleert = 0
    for (const a of artikel) {
      for (let monat = 1; monat <= 12; monat += 1) {
        const schluessel = zelle(a.artikelnummer, monat)
        const text = werte[schluessel] ?? ''
        const grund = {
          fertigartikel: a.artikelnummer,
          jahr,
          monat,
          quelle: QUELLE_FORECAST,
          bemerkung: null,
        }
        if (text.trim() !== '') {
          const menge = zahlLesen(text)
          if (menge === null || menge < 0) {
            setMeldung(`${a.artikelnummer}, ${MONATE_KURZ[monat - 1]}: „${text}“ ist keine Menge.`)
            return
          }
          daten.push({ ...grund, menge_t: menge })
        } else if (gespeichert.has(schluessel)) {
          daten.push({ ...grund, menge_t: null })
          geleert += 1
        }
      }
    }
    try {
      const zurueck = await api.planmengenSetzen(daten)
      uebernehmen(zurueck)
      setMeldung(`${daten.length - geleert} Monatswerte gespeichert${geleert ? `, ${geleert} geleert` : ''}.`)
    } catch (f) {
      setMeldung(fehlertext(f))
    }
  }

  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <div className="leiste">
        <h3>Forecast des Chefs (t Fertigprodukt je Monat)</h3>
        <select value={jahr} onChange={(e) => setJahr(Number(e.target.value))}>
          {[jahr - 1, jahr, jahr + 1].map((j) => (
            <option key={j} value={j}>
              {j}
            </option>
          ))}
        </select>
        <button className="haupt" onClick={() => void speichern()}>
          Forecast speichern
        </button>
        <label
          className="knopf"
          title="Die Datei „4. PLAN - IST“: die Spalten „FC GF JJJJ“ werden als Forecast übernommen"
        >
          PLAN-IST-Datei einlesen
          <input
            type="file"
            accept=".xlsx,.xlsm"
            hidden
            onChange={(e) => {
              const datei = e.target.files?.[0]
              e.target.value = ''
              if (datei) void einlesen(datei)
            }}
          />
        </label>
        {meldung && <span className="hinweis">{meldung}</span>}
      </div>
      <p className="hinweis">
        Ein leeres Feld heißt: für den Monat gilt die Planmenge aus den Stammdaten. Ein geleertes Feld wird beim
        Speichern entfernt. Die Reichweite rechnet über diese Kurve, nicht über ein Jahresmittel: das vierte Quartal ist
        fast das Doppelte des ersten.
      </p>
      <div className="tabellenrahmen forecast-rahmen">
        <table className="forecast-tabelle">
          <thead>
            <tr>
              <th>Fertigartikel</th>
              {MONATE_KURZ.map((m) => (
                <th key={m} className="zahl">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {artikel.map((a) => (
              <tr key={a.artikelnummer}>
                <td title={`${a.artikelnummer} ${a.bezeichnung}`}>
                  {a.artikelnummer} {a.bezeichnung}
                </td>
                {MONATE_KURZ.map((m, i) => (
                  <td key={i} className="zahl">
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={`${a.artikelnummer} ${m}`}
                      value={werte[zelle(a.artikelnummer, i + 1)] ?? ''}
                      onChange={(e) =>
                        setWerte((alt) => ({
                          ...alt,
                          [zelle(a.artikelnummer, i + 1)]: e.target.value,
                        }))
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="leiste" style={{ marginTop: 10, marginBottom: 0 }}>
        <button className="haupt" onClick={() => void speichern()}>
          Forecast speichern
        </button>
        {meldung && <span className="hinweis">{meldung}</span>}
      </div>
    </div>
  )
}

// --- Plan - Ist ----------------------------------------------------------------------------------
// Das Blatt „4. PLAN - IST" von Frau Barth: je Monat Produktion Vorjahr, Forecast GF, tatsächliche
// Produktion und Differenz; darunter Jahressumme, Monatsmittel und Quartalsmittel. Ein Fertigartikel
// auf einmal - die ganze Mappe mit allen Blättern gibt es als Excel.

const QUARTALE = ['I.', 'II.', 'III.', 'IV.']

function PlanIstTabelle() {
  const heute = new Date()
  const [jahr, setJahr] = useState(heute.getFullYear())
  const [gewaehlt, setGewaehlt] = useState('')
  const { daten, fehler } = useLaden<PlanIst[]>(() => api.planIst(jahr), [jahr], {
    onGeladen: (werte) =>
      setGewaehlt((alt) => (werte.some((z) => z.artikelnummer === alt) ? alt : (werte[0]?.artikelnummer ?? ''))),
  })
  const zeilen = daten ?? []

  const zeile = zeilen.find((z) => z.artikelnummer === gewaehlt)
  const diffKlasse = (wert: number | null) => (wert !== null && wert < 0 ? 'unter-mindestbestand' : '')

  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <div className="leiste">
        <h3>Plan - Ist (t Fertigprodukt je Monat)</h3>
        <select value={jahr} onChange={(e) => setJahr(Number(e.target.value))}>
          {[jahr - 1, jahr, jahr + 1].map((j) => (
            <option key={j} value={j}>
              {j}
            </option>
          ))}
        </select>
        <ArtikelAuswahl artikel={zeilen} wert={gewaehlt} onChange={setGewaehlt} />
        <a className="knopf" href={api.exportPlanIst(jahr)} download>
          Als Excel (PLAN - IST, alle Blätter)
        </a>
      </div>
      <p className="hinweis">
        Vorjahr und tatsächliche Produktion kommen aus den gebuchten Ergebnismengen, der Forecast aus der Tabelle oben
        (leere Monate: Planmenge aus den Stammdaten). Die Quartalsmittel sind die Rechenbasis für Reichweiten.
      </p>
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
      {!fehler && zeilen.length === 0 && (
        <p className="leer">Für {jahr} gibt es weder gebuchte Produktion noch einen Forecast.</p>
      )}
      {zeile && (
        <div className="tabellenrahmen">
          <table>
            <thead>
              <tr>
                <th>
                  {zeile.artikelnummer} {zeile.bezeichnung}
                </th>
                <th className="zahl">Prod. {jahr - 1}</th>
                <th className="zahl">FC GF {jahr}</th>
                <th className="zahl">tats. Prod. {jahr}</th>
                <th className="zahl">Diff. tats. Prod. &lt;&gt; FC</th>
              </tr>
            </thead>
            <tbody>
              {zeile.monate.map((m) => (
                <tr key={m.monat}>
                  <td>{MONATE_LANG[m.monat - 1]}</td>
                  <td className="zahl hinweis">{menge(m.vorjahr)}</td>
                  <td className="zahl">{menge(m.forecast)}</td>
                  <td className="zahl">{menge(m.ist)}</td>
                  <td className={`zahl ${diffKlasse(m.differenz)}`}>{menge(m.differenz)}</td>
                </tr>
              ))}
              <tr className="gruppe-summe">
                <td>Σ pro Jahr (t)</td>
                <td className="zahl">{menge(zeile.summe_vorjahr)}</td>
                <td className="zahl">{menge(zeile.summe_forecast)}</td>
                <td className="zahl">{menge(zeile.summe_ist)}</td>
                <td className={`zahl ${diffKlasse(zeile.summe_differenz)}`}>{menge(zeile.summe_differenz)}</td>
              </tr>
              <tr className="gruppe-summe">
                <td>Σ pro Monat (t)</td>
                <td className="zahl">{menge(zeile.mittel_vorjahr)}</td>
                <td className="zahl">{menge(zeile.mittel_forecast)}</td>
                <td className="zahl">{menge(zeile.mittel_ist)}</td>
                <td />
              </tr>
              {zeile.quartale.map((q) => (
                <tr key={q.quartal}>
                  <td>Ø Monat im {QUARTALE[q.quartal - 1]} Quartal</td>
                  <td className="zahl hinweis">{menge(q.vorjahr)}</td>
                  <td className="zahl">{menge(q.forecast)}</td>
                  <td className="zahl">{menge(q.ist)}</td>
                  <td className={`zahl ${diffKlasse(q.differenz)}`}>{menge(q.differenz)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
